import { motion } from "motion/react";
import type { ReactNode } from "react";
import { t } from "@/lib/i18n";
import type { Localized } from "@/lib/i18n";
import type { SectionCtx } from "./types";

interface SectionFrameProps {
  number: string;
  kicker: Localized;
  title: Localized;
  ctx: SectionCtx;
  children: ReactNode;
}

/**
 * Shared chrome around every section: oversized background number, kicker
 * rule and serif title. Children render the section-specific body.
 *
 * The motion `variants` here inherit the active state ("animate") propagated
 * by the panel wrapper in TimerCard, so everything cascades in on entry.
 */
export function SectionFrame({ number, kicker, title, ctx, children }: SectionFrameProps) {
  const { lang, presets } = ctx;

  return (
    <div style={{ position: "relative", width: "100%", overflow: "hidden" }}>
      <motion.span
        variants={presets.bigNum}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -40,
          right: -6,
          fontFamily: "var(--tc-serif)",
          fontSize: 200,
          lineHeight: 1,
          color: "var(--tc-ink-strong)",
          pointerEvents: "none",
        }}
      >
        {number}
      </motion.span>

      <div style={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={presets.rise(0.05)}
          style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 16 }}
        >
          <span
            style={{
              fontFamily: "var(--tc-mono)",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "var(--tc-faint)",
            }}
          >
            {t(kicker, lang)}
          </span>
          <span style={{ flex: 1, height: 1, background: "var(--tc-line-2)" }} />
        </motion.div>

        <motion.h2
          variants={presets.rise(0.1)}
          style={{
            fontFamily: "var(--tc-serif)",
            fontWeight: 400,
            fontSize: 34,
            lineHeight: 1.05,
            color: "var(--tc-ink-strong)",
            marginBottom: 16,
          }}
        >
          {t(title, lang)}
        </motion.h2>

        {children}
      </div>
    </div>
  );
}
